import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Plus, Trash2, Edit2, Save, Clock, Loader2, Film, Link2, CalendarRange, CalendarDays, X, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { cinemasApi, sessionsApi } from '../../services/api';
import ExcelImport from './ExcelImport';

const AdminSessions = () => {
  const [cinemas, setCinemas] = useState([]);
  const [selectedCinema, setSelectedCinema] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [dateMode, setDateMode] = useState('range'); // 'range' or 'single'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedDates, setSelectedDates] = useState([]); // For single date mode
  const [singleDateInput, setSingleDateInput] = useState('');
  const [showtimes, setShowtimes] = useState([{ time: '', format: 'Doblada' }]);
  const [ticketUrl, setTicketUrl] = useState('');
  const [available, setAvailable] = useState(true);
  const [linkOnlyMode, setLinkOnlyMode] = useState(false); // NEW: Solo enlace sin horarios

  const fetchCinemas = useCallback(async () => {
    try {
      const res = await cinemasApi.getAll();
      setCinemas(res.data);
      return res.data;
    } catch (error) {
      console.error('Error fetching cinemas:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const data = await fetchCinemas();
      if (data.length > 0 && !selectedCinema) {
        setSelectedCinema(data[0].id);
      }
      setLoading(false);
    };
    init();
  }, [fetchCinemas, selectedCinema]);

  const currentCinema = cinemas.find(c => c.id === selectedCinema);

  const getDefaultDates = () => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() + 1);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const openNewSessionDialog = () => {
    const defaults = getDefaultDates();
    setDateMode('range');
    setStartDate(defaults.start);
    setEndDate(defaults.end);
    setSelectedDates([]);
    setSingleDateInput('');
    setShowtimes([{ time: '17:00', format: 'Doblada' }]);
    setTicketUrl(currentCinema?.ticketUrl || currentCinema?.website || '');
    setAvailable(true);
    setLinkOnlyMode(false);
    setEditingSession(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (session) => {
    setDateMode('single');
    setSelectedDates([session.date]);
    setShowtimes([{ time: session.time || '', format: session.format || 'Doblada' }]);
    setTicketUrl(session.ticketUrl || '');
    setAvailable(session.available !== false);
    setEditingSession(session);
    setIsDialogOpen(true);
  };

  // Generate dates between start and end
  const generateDateRange = (start, end) => {
    const dates = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);
    
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  // Add a single date to the list
  const addSingleDate = () => {
    if (singleDateInput && !selectedDates.includes(singleDateInput)) {
      setSelectedDates([...selectedDates, singleDateInput].sort());
      setSingleDateInput('');
    }
  };

  // Remove a date from the list
  const removeSingleDate = (date) => {
    setSelectedDates(selectedDates.filter(d => d !== date));
  };

  // Add a new showtime slot
  const addShowtime = () => {
    setShowtimes([...showtimes, { time: '', format: 'Doblada' }]);
  };

  // Remove a showtime slot
  const removeShowtime = (index) => {
    if (showtimes.length > 1) {
      setShowtimes(showtimes.filter((_, i) => i !== index));
    }
  };

  // Update a showtime
  const updateShowtime = (index, field, value) => {
    const updated = [...showtimes];
    updated[index][field] = value;
    setShowtimes(updated);
  };

  const handleSave = async () => {
    if (!selectedCinema) return;
    setSaving(true);
    
    try {
      if (editingSession) {
        // Update single session
        await sessionsApi.update(editingSession.id, {
          time: showtimes[0].time,
          format: showtimes[0].format,
          date: selectedDates[0] || editingSession.date,
          available,
          ticketUrl
        });
      } else {
        // Create multiple sessions
        const dates = dateMode === 'range' 
          ? generateDateRange(startDate, endDate)
          : selectedDates;
        
        if (dates.length === 0) {
          alert('Por favor, selecciona al menos una fecha');
          setSaving(false);
          return;
        }
        
        if (!ticketUrl) {
          alert('Por favor, introduce la URL de compra de entradas');
          setSaving(false);
          return;
        }
        
        // Link-only mode: create one session per date without time
        if (linkOnlyMode) {
          const promises = dates.map(date => 
            sessionsApi.create(selectedCinema, {
              time: '',
              format: 'Doblada',
              date,
              available,
              ticketUrl
            })
          );
          await Promise.all(promises);
        } else {
          // Normal mode with showtimes
          const validShowtimes = showtimes.filter(s => s.time.trim() !== '');
          
          if (validShowtimes.length === 0) {
            alert('Por favor, añade al menos un horario');
            setSaving(false);
            return;
          }
          
          // Create sessions for each date and each showtime
          const promises = [];
          for (const date of dates) {
            for (const showtime of validShowtimes) {
              promises.push(
                sessionsApi.create(selectedCinema, {
                  time: showtime.time,
                  format: showtime.format,
                  date,
                  available,
                  ticketUrl
                })
              );
            }
          }
          
          await Promise.all(promises);
        }
      }
      
      const updatedCinemas = await fetchCinemas();
      setCinemas(updatedCinemas);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving sessions:', error);
      alert('Error al guardar las sesiones');
    } finally {
      setSaving(false);
    }
  };

  const deleteSession = async (sessionId) => {
    if (window.confirm('¿Estás seguro de eliminar esta sesión?')) {
      try {
        await sessionsApi.delete(sessionId);
        const updatedCinemas = await fetchCinemas();
        setCinemas(updatedCinemas);
      } catch (error) {
        console.error('Error deleting session:', error);
        alert('Error al eliminar');
      }
    }
  };

  // Group sessions by date
  const sessionsByDate = currentCinema?.showtimes?.reduce((acc, session) => {
    const date = session.date || 'Sin fecha';
    if (!acc[date]) acc[date] = [];
    acc[date].push(session);
    return acc;
  }, {}) || {};

  const sortedDates = Object.keys(sessionsByDate).sort();

  const formatDate = (dateStr) => {
    if (dateStr === 'Sin fecha') return dateStr;
    const date = new Date(dateStr);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('es-ES', options);
  };

  const formatShortDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  // Calculate summary for dialog
  const getSessionSummary = () => {
    const dates = dateMode === 'range' 
      ? generateDateRange(startDate, endDate)
      : selectedDates;
    
    if (linkOnlyMode) {
      // Link-only mode: 1 session per day
      return {
        totalDays: dates.length,
        totalShowtimes: 0,
        totalSessions: dates.length,
        isLinkOnly: true
      };
    }
    
    const validShowtimes = showtimes.filter(s => s.time.trim() !== '');
    return {
      totalDays: dates.length,
      totalShowtimes: validShowtimes.length,
      totalSessions: dates.length * validShowtimes.length,
      isLinkOnly: false
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const summary = getSessionSummary();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sesiones</h1>
          <p className="text-gray-500">Gestiona las proyecciones por día y cine</p>
        </div>
        <Button 
          onClick={openNewSessionDialog} 
          className="bg-[#50c878] hover:bg-[#40a0a0]"
          disabled={!selectedCinema}
          data-testid="add-session-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Programar Sesiones
        </Button>
      </div>

      {/* Excel Import Section */}
      <ExcelImport onImportComplete={async () => {
        const updatedCinemas = await fetchCinemas();
        setCinemas(updatedCinemas);
      }} />

      {/* Cinema Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Film className="w-5 h-5 text-[#50c878]" />
            Seleccionar Cine
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedCinema} onValueChange={setSelectedCinema}>
            <SelectTrigger className="w-full md:w-96" data-testid="cinema-selector">
              <SelectValue placeholder="Selecciona un cine" />
            </SelectTrigger>
            <SelectContent>
              {cinemas.map((cinema) => (
                <SelectItem key={cinema.id} value={cinema.id}>
                  {cinema.name} - {cinema.address}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {currentCinema?.ticketOnlyMode && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                Este cine está en modo "Solo enlace de compra". Las sesiones individuales no se mostrarán en la página principal.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sessions by Date */}
      {currentCinema && (
        <div className="space-y-6">
          {sortedDates.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <div className="text-center text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay sesiones configuradas para este cine</p>
                  <Button onClick={openNewSessionDialog} variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Programar sesiones
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            sortedDates.map(date => (
              <Card key={date}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Calendar className="w-5 h-5 text-[#50c878]" />
                    {formatDate(date)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {sessionsByDate[date].map((session) => (
                      <div
                        key={session.id}
                        className={`p-4 rounded-lg border-2 ${
                          session.available 
                            ? 'border-green-200 bg-green-50' 
                            : 'border-gray-200 bg-gray-50'
                        }`}
                        data-testid={`session-card-${session.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-2xl font-bold text-gray-900">
                            {session.time || 'Sin hora'}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded font-medium ${
                            session.available 
                              ? 'bg-green-500 text-white' 
                              : 'bg-gray-400 text-white'
                          }`}>
                            {session.available ? 'Disponible' : 'Agotado'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm px-3 py-1 rounded font-medium ${
                            session.format === 'VOSE' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {session.format}
                          </span>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(session)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteSession(session.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {session.ticketUrl && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
                            <Link2 className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{session.ticketUrl}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSession ? 'Editar Sesión' : 'Programar Sesiones'}
            </DialogTitle>
            <DialogDescription>
              {editingSession 
                ? `Modifica los detalles de la sesión para ${currentCinema?.name}`
                : `Programa múltiples sesiones para ${currentCinema?.name}`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Date Mode Selection (only for new sessions) */}
            {!editingSession && (
              <div className="space-y-4">
                <Label>Modo de selección de fechas</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setDateMode('range')}
                    className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                      dateMode === 'range' 
                        ? 'border-[#50c878] bg-[#50c878]/10' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <CalendarRange className={`w-6 h-6 ${dateMode === 'range' ? 'text-[#50c878]' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${dateMode === 'range' ? 'text-[#50c878]' : 'text-gray-600'}`}>
                      Rango de fechas
                    </span>
                    <span className="text-xs text-gray-400">Del 15 al 21 mayo</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setDateMode('single')}
                    className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                      dateMode === 'single' 
                        ? 'border-[#50c878] bg-[#50c878]/10' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <CalendarDays className={`w-6 h-6 ${dateMode === 'single' ? 'text-[#50c878]' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${dateMode === 'single' ? 'text-[#50c878]' : 'text-gray-600'}`}>
                      Días sueltos
                    </span>
                    <span className="text-xs text-gray-400">Selecciona días específicos</span>
                  </button>
                </div>
              </div>
            )}

            {/* Date Range Mode */}
            {dateMode === 'range' && !editingSession && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startDate">Fecha inicio</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="endDate">Fecha fin</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                {startDate && endDate && (
                  <p className="text-sm text-[#50c878]">
                    Se crearán sesiones para {generateDateRange(startDate, endDate).length} días
                  </p>
                )}
              </div>
            )}

            {/* Single Date Mode */}
            {dateMode === 'single' && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                {!editingSession && (
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label htmlFor="singleDate">Añadir fecha</Label>
                      <Input
                        id="singleDate"
                        type="date"
                        value={singleDateInput}
                        onChange={(e) => setSingleDateInput(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <Button 
                      type="button" 
                      onClick={addSingleDate}
                      className="mt-7 bg-[#50c878] hover:bg-[#40a0a0]"
                      disabled={!singleDateInput}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {selectedDates.length > 0 && (
                  <div className="space-y-2">
                    <Label>Fechas seleccionadas ({selectedDates.length})</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedDates.map(date => (
                        <span 
                          key={date}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-[#50c878]/20 text-[#50c878] rounded-full text-sm"
                        >
                          {formatShortDate(date)}
                          {!editingSession && (
                            <button 
                              type="button"
                              onClick={() => removeSingleDate(date)}
                              className="hover:text-red-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Link Only Mode Toggle (only for new sessions) */}
            {!editingSession && (
              <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <ExternalLink className="w-5 h-5 text-amber-600" />
                  <div>
                    <Label htmlFor="linkOnlyMode" className="text-amber-800">Solo enlace de compra</Label>
                    <p className="text-xs text-amber-600">Sin horarios específicos, solo botón "Comprar entradas"</p>
                  </div>
                </div>
                <Switch
                  id="linkOnlyMode"
                  checked={linkOnlyMode}
                  onCheckedChange={setLinkOnlyMode}
                />
              </div>
            )}

            {/* Showtimes (hidden in link-only mode) */}
            {!linkOnlyMode && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Horarios</Label>
                  {!editingSession && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={addShowtime}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Añadir horario
                    </Button>
                  )}
                </div>
                
                <div className="space-y-3">
                  {showtimes.map((showtime, index) => (
                    <div key={index} className="flex items-end gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <Label htmlFor={`time-${index}`} className="text-xs">Hora</Label>
                        <Input
                          id={`time-${index}`}
                          type="time"
                          value={showtime.time}
                          onChange={(e) => updateShowtime(index, 'time', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex-1">
                        <Label htmlFor={`format-${index}`} className="text-xs">Versión</Label>
                        <Select 
                          value={showtime.format} 
                          onValueChange={(value) => updateShowtime(index, 'format', value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Doblada">Doblada</SelectItem>
                            <SelectItem value="VOSE">VOSE</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {showtimes.length > 1 && !editingSession && (
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeShowtime(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ticket URL */}
            <div>
              <Label htmlFor="ticketUrl">URL de compra de entradas</Label>
              <Input
                id="ticketUrl"
                value={ticketUrl}
                onChange={(e) => setTicketUrl(e.target.value)}
                placeholder="https://cine.com/comprar"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Se usará esta URL para todas las sesiones creadas
              </p>
            </div>

            {/* Availability */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor="available">Disponible</Label>
                <p className="text-xs text-gray-500">¿Hay entradas disponibles?</p>
              </div>
              <Switch
                id="available"
                checked={available}
                onCheckedChange={setAvailable}
              />
            </div>

            {/* Summary (only for new sessions) */}
            {!editingSession && summary.totalSessions > 0 && (
              <div className={`p-4 border rounded-lg ${summary.isLinkOnly ? 'bg-amber-50 border-amber-200' : 'bg-[#50c878]/10 border-[#50c878]/30'}`}>
                <p className={`text-sm font-medium ${summary.isLinkOnly ? 'text-amber-700' : 'text-[#50c878]'}`}>
                  Resumen: Se crearán {summary.totalSessions} {summary.isLinkOnly ? 'enlaces' : 'sesiones'}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {summary.isLinkOnly 
                    ? `${summary.totalDays} días con enlace directo a compra`
                    : `${summary.totalDays} días × ${summary.totalShowtimes} horarios`
                  }
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving} 
              className="bg-[#50c878] hover:bg-[#40a0a0]"
              data-testid="save-session-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {editingSession 
                ? 'Guardar' 
                : `Crear ${summary.totalSessions} ${summary.isLinkOnly ? 'enlaces' : 'sesiones'}`
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSessions;
